import React, { useMemo } from 'react';
import uniq from 'lodash/uniq';
import without from 'lodash/without';

import { withFormField } from '@gogame/form';
import {
  VirtualizedListItem,
  Box,
  Grid,
  Divider,
  IconButton,
  ReadMoreIcon,
  PlaylistRemoveIcon,
} from '@gogame/components';

import { useSearch } from '../UserPermissions';
import PreviewPermissionList from './components/PreviewPermissionList';
import SelectablePermissionList from './components/SelectablePermissionList';

const defaultGetOptionLabel = ({ name }) => name;
const defaultGetOptionValue = ({ id }) => id;

const defaultAvailableLabel = ({ label }) => `Available ${label.toLowerCase()}`;
const defaultSelectedLabel = ({ label }) => `Selected ${label.toLowerCase()}`;

const PermissionPicker = withFormField({
  padding: 0,
  grow: 1,
})(({
  label,
  value = [],
  options: optionsProp = [],
  searchQuery,
  onChange,
  area,
  getOptionLabel = defaultGetOptionLabel,
  getOptionValue = defaultGetOptionValue,
  availableLabel = defaultAvailableLabel,
  selectedLabel = defaultSelectedLabel,
}) => {
  const options = useMemo(() => {
    return area.data || optionsProp;
  }, [area.data, optionsProp]);

  const { filteredOptions } = useSearch({ options });

  const filteredOptionIds = useMemo(() => {
    return filteredOptions.map(getOptionValue);
  }, [filteredOptions]);

  const visibleSelectedOptions = useMemo(() => {
    return filteredOptions.filter((option) =>
      value.includes(getOptionValue(option))
    );
  }, [filteredOptions, value, searchQuery]);

  const selectableOptions = useMemo(() => {
    return filteredOptions.filter(
      (option) => !value.includes(getOptionValue(option))
    );
  }, [filteredOptions, value]);

  const handleSelectAll = (e) => {
    onChange(e, uniq([...value, ...filteredOptionIds]));
  };

  const handleClearAll = (e) => {
    onChange(e, without(value, ...filteredOptionIds));
  };

  const handleDelete = (e, deletedOption) => {
    const newValue = options
      .filter((opt) => getOptionValue(opt) !== getOptionValue(deletedOption))
      .map((opt) => getOptionValue(opt))
      .filter((optV) => value.includes(optV));

    onChange(e, newValue);
  };

  const handleSelect = (e, v) => {
    const currentIndex = value.indexOf(v);
    let newValue = [...value];

    if (currentIndex === -1) {
      newValue.push(v);
    } else {
      newValue.splice(currentIndex, 1);
    }

    onChange(e, newValue);
  };

  return (
    <Grid container fullHeight>
      <Grid
        item
        xs={6}
        fullHeight
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}
        data-cy={'availableItems'}
      >
        <div data-cy={'listTitle'}>
          <VirtualizedListItem
            label={availableLabel({ label })}
            style={{ height: 48 }}
            secondaryAction={
              <IconButton
                color={'primary'}
                onClick={handleSelectAll}
                tooltip={'Select All'}
                icon={<ReadMoreIcon />}
                disabled={!selectableOptions.length}
              />
            }
          />
        </div>

        <Divider style={{ marginTop: -1 }} />
        <Box
          display={'flex'}
          flex={1}
          style={{ marginRight: 1 }}
          data-cy={'list'}
        >
          <SelectablePermissionList
            options={selectableOptions}
            onChange={handleSelect}
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
          />
        </Box>
      </Grid>
      <Divider orientation={'vertical'} flexItem />
      <Grid
        item
        xs
        fullHeight
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}
        data-cy={'selectedItems'}
      >
        <div data-cy={'listTitle'}>
          <VirtualizedListItem
            label={selectedLabel({ label })}
            style={{ height: 48 }}
            secondaryAction={
              <IconButton
                color={'primary'}
                onClick={handleClearAll}
                tooltip={'Clear All'}
                icon={<PlaylistRemoveIcon />}
                disabled={!visibleSelectedOptions.length}
              />
            }
          />
        </div>
        <Divider style={{ marginTop: -1 }} />
        <Box display={'flex'} flex={1} data-cy={'list'}>
          <PreviewPermissionList
            options={visibleSelectedOptions}
            onDelete={handleDelete}
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
          />
        </Box>
      </Grid>
    </Grid>
  );
});

export default PermissionPicker;
